/* ═══════════════════════════════════════════════════════════════════════════
   The API boundary
   ───────────────────────────────────────────────────────────────────────────
   Everything in the app that touches data imports from here, and this module
   decides — once, from `VITE_API_MODE` — whether that data comes from the
   seeded browser implementation or from a real service.

       VITE_API_MODE=mock        (default)  →  ./mock
       VITE_API_MODE=http                   →  ./http, at VITE_API_URL

   Swapping backends is that line in an `.env` file. No import changes, no
   signature changes, no hook changes, and the mock stays working afterwards —
   which matters more than it sounds: it is what the tests run against, what
   makes the app demoable with no server, and what the offline story is built
   on. An approach that replaced the mock in place would buy the same swap once
   and cost all three permanently.

   The re-export style below is deliberate. `export const api = impl` alone
   would work, but every call site would become `api.getOrders()` and the
   existing `import { lineTotal } from '../lib/api'` would break — so the
   selected implementation is destructured back into named exports, and callers
   are untouched by the whole arrangement.

   Two things are exported unconditionally because they do not vary by
   transport:

   · `./local` — pricing, the cancellable set. Pure client-side rules.
   · `./contract` types — the shapes on the wire, identical in both modes.
   ═══════════════════════════════════════════════════════════════════════════ */

import type { HexApi } from './contract'
import { httpApi } from './http'
import { mockApi } from './mock'

/* The branch reads `import.meta.env` directly rather than `config.apiMode`,
   and that is load-bearing rather than a style slip.

   Vite substitutes `import.meta.env.VITE_API_MODE` with a string literal at
   build time, so this comparison folds to a constant and Rollup drops the
   branch that was not taken — which for an `http` build means the mock and the
   57 kB seed catalog behind it never enter the bundle. Reading the identical
   value off the frozen `config` object is the same thing at runtime and
   completely opaque to the bundler, and the production build then ships both
   implementations. It was written that way first; the two builds came out the
   same size, which is how the difference was noticed.

   `config` still owns parsing and validation — it is imported by `client.ts`
   and `mock.ts`, and it is what turns a misspelt mode into a thrown error. */
const impl: HexApi = import.meta.env.VITE_API_MODE === 'http' ? httpApi : mockApi

/* ── the contract, bound to whichever implementation is live ──────────────── */

export const {
  getCategories,
  getAreas,
  getCities,
  getProviders,
  getProvider,
  getMenu,
  getReviews,
  getFeaturedReviews,
  search,
  getSlots,
  getPromos,
  getQuote,
  holdSlot,
  releaseSlot,
  signInWithGoogle,
  signInWithPassword,
  signUpWithPassword,
  resendVerification,
  requestPasswordReset,
  checkPasswordReset,
  completePasswordReset,
  confirmEmail,
  getMe,
  updateProfile,
  refreshSession,
  signOut,
  registerDevice,
  unregisterDevice,
  getAddresses,
  saveAddress,
  deleteAddress,
  getFavourites,
  setFavourite,
  placeOrder,
  getOrders,
  getOrder,
  cancelOrder,
  rateOrder,
  submitApplication,
} = impl

/** Whether this build can walk an order along its own flow.
 *
 *  A capability check, not a feature flag: there is no configuration that
 *  turns it on, only an adapter that has the method or does not. `./mock` does
 *  and `./http` never will — see the note on the contract — so the tracking
 *  screen asks this rather than assuming, and runs its simulated courier only
 *  where something is there to simulate. */
export const canAdvanceOrder = typeof impl.advanceOrder === 'function'

/** Walks an order one rung along its flow, in the builds that can.
 *
 *  Throws where the adapter has no such method rather than resolving quietly:
 *  callers are expected to have checked `canAdvanceOrder`, so reaching this is
 *  a bug, and one that failed silently would look like a tracking screen that
 *  had simply stopped. */
export function advanceOrder(id: string, signal?: AbortSignal) {
  const advance = impl.advanceOrder
  if (!advance) throw new Error('[hexclean] this build cannot advance an order')
  return advance(id, signal)
}

/** The whole surface as one object, for code that wants to pass it around or
 *  stub it wholesale rather than import the pieces. */
export const api = impl

/* ── transport-independent ────────────────────────────────────────────────── */

export {
  amountDue,
  CANCELLABLE,
  DEV_EMAIL,
  DEV_PASSWORD,
  MIN_PASSWORD,
  lineTotal,
  priceOrder,
  termsOf,
  type ListTerms,
  type PriceInput,
} from './local'

export type {
  ApplicationInput,
  FeaturedReview,
  HexApi,
  Menu,
  PlaceOrderInput,
  Quote,
  QuoteInput,
  SearchHit,
  SlotHold,
  SlotHoldInput,
} from './contract'
